import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, currentWeek, CHART_COLORS } from "@/lib/format";
import { WeekSelect } from "@/components/WeekSelect";
import { ColorPill, ProgressBar } from "@/components/StatusPill";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { FileDown, FileText, FileSpreadsheet, Loader2 } from "lucide-react";

export default function Reports() {
  const { settings } = useAuth();
  const cur = settings.currency || "£";
  const [sites, setSites] = useState([]);
  const [week, setWeek] = useState(currentWeek());
  const [site, setSite] = useState("");

  useEffect(() => { api.get("/sites").then((r) => { setSites(r.data); if (r.data[0]) setSite(r.data[0].id); }); }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Reports</h1>
        <p className="text-slate-500 text-sm mt-1">Weekly, merchant, category, daily and cross-home analysis.</p>
      </div>

      <Tabs defaultValue="weekly">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly</TabsTrigger>
          <TabsTrigger value="merchant" data-testid="tab-merchant">Merchant</TabsTrigger>
          <TabsTrigger value="category" data-testid="tab-category">Category</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="comparison" data-testid="tab-comparison">Site Comparison</TabsTrigger>
        </TabsList>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <WeekSelect value={week} onChange={setWeek} />
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="w-[180px] bg-white"><SelectValue placeholder="Home" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <TabsContent value="weekly" className="mt-4"><Weekly week={week} site={site} cur={cur} /></TabsContent>
        <TabsContent value="merchant" className="mt-4"><Merchant week={week} cur={cur} /></TabsContent>
        <TabsContent value="category" className="mt-4"><CategoryR week={week} cur={cur} /></TabsContent>
        <TabsContent value="daily" className="mt-4"><Daily week={week} cur={cur} /></TabsContent>
        <TabsContent value="comparison" className="mt-4"><Comparison week={week} cur={cur} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Weekly({ week, site, cur }) {
  const [r, setR] = useState(null);
  useEffect(() => { if (!site) return; setR(null); api.get(`/reports/weekly?site_id=${site}&week=${week}`).then((x) => setR(x.data)); }, [week, site]);
  if (!site) return <Empty text="Select a home" />;
  if (!r) return <Spin />;
  const token = localStorage.getItem("hs_token");
  const exp = (fmt) => window.open(`${API}/export/weekly?site_id=${site}&week=${week}&fmt=${fmt}&auth=${token}`, "_blank");

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-head text-xl font-medium text-slate-800">{r.site_name}</h2><p className="text-sm text-slate-500">{r.week_label} · <span className="capitalize">{r.status}</span></p></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="export-pdf" onClick={() => exp("pdf")}><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
          <Button size="sm" variant="outline" data-testid="export-csv" onClick={() => exp("csv")}><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button size="sm" variant="outline" data-testid="export-xlsx" onClick={() => exp("xlsx")}><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Opening Allocation" value={money(r.allocation, cur)} />
        <Stat label="Approved Spend" value={money(r.spent, cur)} />
        <Stat label="Remaining" value={money(r.remaining, cur)} accent="text-emerald-700" />
        <Stat label="Receipts" value={r.receipts} sub={`${r.receipt_rate}% complete`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="border-border p-5">
          <h3 className="font-head font-medium text-slate-800 mb-3">Category breakdown</h3>
          {r.category_breakdown.length === 0 ? <Empty text="No approved spend yet" /> : (
            <div className="space-y-2">
              {r.category_breakdown.map((c) => (
                <div key={c.category} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0">
                  <span className="text-slate-700">{c.category}</span>
                  <span className="font-mono tabular-nums font-medium">{money(c.amount, cur)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="border-border p-5">
          <h3 className="font-head font-medium text-slate-800 mb-3">Transactions ({r.transactions.length})</h3>
          <div className="max-h-[320px] overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Merchant</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{r.transactions.map((t) => (
                <TableRow key={t.id}><TableCell className="text-slate-700">{t.merchant}</TableCell><TableCell className="text-right font-mono tabular-nums">{money(t.amount, cur)}</TableCell><TableCell className="text-xs capitalize text-slate-500">{t.status.replace("_", " ")}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Merchant({ week, cur }) {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/merchant?week=${week}`).then((r) => setData(r.data)); }, [week]);
  if (!data) return <Spin />;
  const openDetail = (m) => api.get(`/expenses?week=${week}&status=approved&merchant=${encodeURIComponent(m)}`).then((r) => setDetail({ merchant: m, rows: r.data }));
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="border-border p-5">
        <h3 className="font-head font-medium text-slate-800 mb-3">Spend by merchant</h3>
        {data.length === 0 ? <Empty text="No approved spend" /> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide /><YAxis type="category" dataKey="merchant" width={90} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => money(v, cur)} /><Bar dataKey="total" fill="#0F172A" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
      <Card className="border-border p-5">
        <h3 className="font-head font-medium text-slate-800 mb-3">{detail ? `${detail.merchant} — transactions` : "Click a merchant"}</h3>
        {!detail ? (
          <div className="space-y-1">{data.map((m) => (
            <button key={m.merchant} data-testid={`merchant-${m.merchant}`} onClick={() => openDetail(m.merchant)} className="w-full flex justify-between items-center py-2 px-2 rounded-lg hover:bg-slate-50 text-sm">
              <span className="text-slate-700">{m.merchant}</span><span className="font-mono tabular-nums font-medium">{money(m.total, cur)}</span>
            </button>
          ))}</div>
        ) : (
          <div>
            <Button size="sm" variant="ghost" onClick={() => setDetail(null)} className="mb-2">← Back</Button>
            <Table><TableBody>{detail.rows.map((t) => (<TableRow key={t.id}><TableCell>{t.purchase_date}</TableCell><TableCell>{t.category}</TableCell><TableCell className="text-right font-mono">{money(t.amount, cur)}</TableCell></TableRow>))}</TableBody></Table>
          </div>
        )}
      </Card>
    </div>
  );
}

function CategoryR({ week, cur }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/category?week=${week}`).then((r) => setData(r.data)); }, [week]);
  if (!data) return <Spin />;
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="border-border p-5">
        <h3 className="font-head font-medium text-slate-800 mb-3">Spend by category</h3>
        {data.categories.length === 0 ? <Empty text="No approved spend" /> : (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart><Pie data={data.categories} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={(e) => `${e.pct}%`}>
              {data.categories.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie><Tooltip formatter={(v) => money(v, cur)} /></PieChart>
          </ResponsiveContainer>
        )}
      </Card>
      <Card className="border-border p-5">
        <h3 className="font-head font-medium text-slate-800 mb-3">Breakdown · Total {money(data.total, cur)}</h3>
        <div className="space-y-2">{data.categories.map((c, i) => (
          <div key={c.category} className="flex items-center gap-3 text-sm py-1">
            <span className="h-3 w-3 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
            <span className="flex-1 text-slate-700">{c.category}</span>
            <span className="text-slate-400 text-xs">{c.pct}%</span>
            <span className="font-mono tabular-nums font-medium w-20 text-right">{money(c.amount, cur)}</span>
          </div>
        ))}</div>
      </Card>
    </div>
  );
}

function Daily({ week, cur }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/daily?week=${week}`).then((r) => setData(r.data)); }, [week]);
  if (!data) return <Spin />;
  return (
    <Card className="border-border p-5">
      <h3 className="font-head font-medium text-slate-800 mb-3">Daily spending</h3>
      {data.length === 0 ? <Empty text="No approved spend" /> : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => money(v, cur)} /><Line type="monotone" dataKey="total" stroke="#3E8755" strokeWidth={2} dot={{ r: 3 }} /></LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function Comparison({ week, cur }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/comparison?week=${week}`).then((r) => setData(r.data)); }, [week]);
  if (!data) return <Spin />;
  return (
    <Card className="border-border overflow-hidden">
      <Table>
        <TableHeader><TableRow><TableHead>Home</TableHead><TableHead className="text-right">Budget</TableHead><TableHead className="text-right">Spent</TableHead><TableHead className="text-right">Remaining</TableHead><TableHead className="w-[180px]">% Used</TableHead></TableRow></TableHeader>
        <TableBody>{data.map((s) => (
          <TableRow key={s.id}><TableCell className="font-medium text-slate-800">{s.name}</TableCell>
            <TableCell className="text-right font-mono tabular-nums">{money(s.allocation, cur)}</TableCell>
            <TableCell className="text-right font-mono tabular-nums">{money(s.spent, cur)}</TableCell>
            <TableCell className="text-right font-mono tabular-nums">{money(s.remaining, cur)}</TableCell>
            <TableCell><div className="flex items-center gap-2"><ProgressBar pct={s.pct_used} color={s.status_color} /><ColorPill color={s.status_color}>{s.pct_used}%</ColorPill></div></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </Card>
  );
}

const Spin = () => <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
const Empty = ({ text }) => <div className="text-center text-slate-400 py-10 text-sm">{text}</div>;
const Stat = ({ label, value, sub, accent }) => (
  <Card className="border-border p-4"><div className="text-xs uppercase tracking-wider font-semibold text-slate-400">{label}</div><div className={`font-mono tabular-nums text-2xl font-semibold mt-1 ${accent || "text-slate-900"}`}>{value}</div>{sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}</Card>
);
